import React, { useState } from "react";
import { Grid, Button } from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import DataTable from "react-data-table-component";
import * as Yup from "yup";
import XLSX from "xlsx";
import { useHistory } from "react-router-dom";
import apiAuth from "../../helpers/ApiAuth";
import NotificationManager from "../../components/Common/NotificationManager";
import NewHeader from "../../Layouts/NewHeader";
import NewFooter from "../../Layouts/NewFooter";

import BreadCrumb from "../../components/Common/BreadCrumb";
import CustomFileUpload from "../../components/Common/CustomFileUpload";

const customStyles = {
  table: {
    style: {
      minHeight: "170px",
      maxHeight: "100%",
    },
  },

  headCells: {
    style: {
      display: "flex",
      justifyContent: "start",
      alignItems: "center",
      marginTop: "10px",
      fontSize: "16px !important",
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
  },

  cells: {
    style: {
      display: "flex",
      justifyContent: "start",
      alignItems: "center",
      minHeight: "47px",
      fontSize: "14px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      overflow: "visible",
    },
  },
  headRow: {
    style: {
      background: "#ECECEC",
      fontWeight: "700",
      borderRadius: "10px 10px 0px 0px",
    },
  },
  rows: {
    style: {
      color: "#093564",
      border: "1px solid rgba(0, 0, 0, 0.10)",
      alignItems: "flex-start",
      paddingTop: "8px",
      paddingBottom: "8px",
    },
  },

  pagination: {
    style: {
      color: "#000",
      backgroundColor: "#f3f3f9",
    },
  },
  noData: {
    background: "none",
    fontSize: "18px",
    color: "#888",
    textAlign: "center",
    padding: "20px",
  },
};

const REQUIRED_FIELDS = ["consumerno", "meterno"];

const validateRow = (row, rowIndex) => {
  const errors = [];
  REQUIRED_FIELDS.forEach((field) => {
    if (!row[field] || row[field].trim() === "") {
      errors.push(`Missing required field: ${field}`);
    }
  });
  // Phone format validation if provided
  if (row.phone && row.phone.trim() !== "" && !/^\d{10,}$/.test(row.phone.trim())) {
    errors.push("Invalid phone number");
  }
  // Email format validation if provided
  if (row.email && row.email.trim() !== "" && !/\S+@\S+\.\S+/.test(row.email.trim())) {
    errors.push("Invalid email format");
  }
  return errors;
};

const UploadData = () => {
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [consumerData, setConsumerData] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [parsedRows, setParsedRows] = useState(null); // parsed data before confirm
  const [isUploaded, setIsUploaded] = useState(false); // tracks if upload is done
  const history = useHistory();

  const handleChange = (event) => {
    setConsumerData(null);
    setUploadResults(null);
    setParsedRows(null);
    setIsUploaded(false);
    setState({
      ...state,
      file: URL.createObjectURL(event.target.files[0]),
      picfile: event.target.files[0],
      showData: false,
      consumerData: [],
    });
  };

  // Step 1: Parse file and show validation results
  const handleFileData = async () => {
    if (state.picfile) {
      setUploadLoading(true);
      setUploadResults(null);
      setParsedRows(null);
      setIsUploaded(false);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          const jsonData = XLSX.utils.sheet_to_json(sheet);

          if (!jsonData || jsonData.length === 0) {
            NotificationManager.error("", "File is empty or has no valid data", 3000, null, null, "");
            setUploadLoading(false);
            return;
          }

          // Convert all values to string
          const jsonStringData = jsonData.map((obj) =>
            Object.fromEntries(
              Object.entries(obj).map(([key, value]) => [key, String(value)])
            )
          );

          // Validate each row
          const results = jsonStringData.map((row, index) => ({
            rowNumber: index + 1, // data rows start from 1
            data: row,
            parsingErrors: validateRow(row, index),
            apiStatus: null,
            apiError: null,
          }));

          // Mark invalid rows
          results.forEach((r) => {
            if (r.parsingErrors.length > 0) {
              r.apiStatus = "Skipped";
            } else {
              r.apiStatus = "Pending";
            }
          });

          setParsedRows(results);
          setUploadResults(results);

          const validCount = results.filter((r) => r.parsingErrors.length === 0).length;
          const invalidCount = results.filter((r) => r.parsingErrors.length > 0).length;

          if (validCount === 0) {
            NotificationManager.error("", "All rows have validation errors. Fix the file and re-upload.", 3000, null, null, "");
          } else if (invalidCount > 0) {
            NotificationManager.warning("", `${invalidCount} row(s) have errors and will be skipped. ${validCount} valid row(s) ready to upload.`, 5000, null, null, "");
          } else {
            NotificationManager.success("", `All ${validCount} rows are valid. Click "Confirm Upload" to proceed.`, 3000, null, null, "");
          }

          setUploadLoading(false);
        } catch (err) {
          console.error("File parsing error:", err);
          NotificationManager.error("", "Failed to parse the uploaded file", 3000, null, null, "");
          setUploadLoading(false);
        }
      };

      reader.readAsArrayBuffer(state.picfile);
    } else {
      NotificationManager.error("", "Please select a file", 3000, null, null, "");
    }
  };

  // Step 2: Confirm and upload only valid rows
  const handleConfirmUpload = async () => {
    if (!parsedRows) return;

    const validRows = parsedRows.filter((r) => r.parsingErrors.length === 0);
    const invalidRows = parsedRows.filter((r) => r.parsingErrors.length > 0);

    if (validRows.length === 0) {
      NotificationManager.error("", "No valid rows to upload", 3000, null, null, "");
      return;
    }

    setUploadLoading(true);
    const validData = validRows.map((r) => r.data);

    await apiAuth
      .post("api/Consumer/", validData)
      .then((res) => {
        console.log("response", res);
        const { data } = res || {};

        // Map API response back to each valid row
        validRows.forEach((row, index) => {
          if (data && data[index]) {
            const responseItem = data[index];
            if (responseItem.status === "Error") {
              row.apiStatus = "Error";
              row.apiError = responseItem.comments_ || responseItem.message || "API returned error";
            } else {
              row.apiStatus = "Success";
              row.apiError = null;
            }
          } else {
            row.apiStatus = "Success";
            row.apiError = null;
          }
        });

        // Combine all results and sort by row number
        const allResults = [...invalidRows, ...validRows].sort(
          (a, b) => a.rowNumber - b.rowNumber
        );
        setUploadResults(allResults);
        setIsUploaded(true);

        const successCount = allResults.filter((r) => r.apiStatus === "Success").length;
        const failCount = allResults.length - successCount;

        if (failCount === 0) {
          NotificationManager.success("", `All ${successCount} records uploaded successfully`, 3000, null, null, "");
        } else if (successCount === 0) {
          NotificationManager.error("", `All ${failCount} records failed`, 3000, null, null, "");
        } else {
          NotificationManager.warning("", `${successCount} succeeded, ${failCount} failed`, 3000, null, null, "");
        }

        setUploadLoading(false);
      })
      .catch((err) => {
        console.log(err.response);

        // Mark all valid rows as failed due to API error
        validRows.forEach((row) => {
          row.apiStatus = "Error";
          row.apiError = err?.response?.data?.message || err?.message || "API request failed";
        });

        const allResults = [...invalidRows, ...validRows].sort(
          (a, b) => a.rowNumber - b.rowNumber
        );
        setUploadResults(allResults);
        setIsUploaded(true);

        NotificationManager.error("", "Upload failed. See results table for details.", 3000, null, null, "");
        setUploadLoading(false);
      });
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          overflowX: "hidden",
        }}
      >
        <NewHeader />
        {/* <div className="container" style={{ flex: "1" }}> */}
        <div
          className="mt-4 p-5"
          style={{
            background:
              "linear-gradient(180deg, rgba(23, 0, 0, 0.80) 28.15%, rgba(0, 11, 5, 0.80) 99.2%)",
            backdropFilter: "blur(30px)",
          }}
        >
          <h5 style={{ color: "#FFF", fontSize: "25px", fontWeight: "700" }}>
            Prepaid Electricity Recharge
          </h5>
          {localStorage.getItem("username") && <BreadCrumb />}
        </div>

        <div className="container mx-1" style={{ flex: "1" }}>
          <Grid container>
            <Grid item lg={9} xs={12}>
              <div
                className="card px-5 py-5 my-3 mx-2 m-auto shadow-lg position-relative"
                style={{
                  top: "-45px",
                  borderRadius: "10px",
                  margin: "auto !important",
                }}
              >
                <Formik
                  initialValues={{
                    consumer_id: "",
                  }}
                  validationSchema={Yup.object({
                    // consumer_id: Yup.string().required("Required!"),
                  })}
                  onSubmit={(values) => {
                    setLoading(true);
                    apiAuth
                      .get(`api/Consumer?input=${values.consumer_id}`)
                      .then((res) => {
                        const { data } = res;
                        if (values.consumer_id === "") {
                          setConsumerData(data);
                        } else {
                          setConsumerData([data]);
                        }

                        setLoading(false);
                      })
                      .catch((err) => {
                        console.log("error", err);
                        setLoading(false);

                        NotificationManager.error(
                          "Transaction details",
                          "Consumer details Get Error",
                          3000,
                          null,
                          null,
                          ""
                        );
                      });
                  }}
                >
                  {({ values, errors, touched, setFieldValue }) => (
                    <Form className="av-tooltip tooltip-label-bottom mb-4">
                      <Grid
                        container
                        sx={{ gap: "25px", placeItems: "center" }}
                      >
                        <Grid item lg={8} xs={12}>
                          <div>
                            <label
                              htmlFor="consumer_id"
                              className="form-label"
                              style={{
                                color: "#9e9e9e",
                                fontSize: "14px",
                              }}
                            >
                              Please enter your Consumer No or Meter No
                              <span className="text-danger">*</span>
                            </label>
                            <Field
                              className="form-control"
                              name="consumer_id"
                              placeholder="Consumer Number"
                              style={{
                                borderRadius: "0",
                                border: "none",
                                borderBottom: "1px solid #9e9e9e",
                              }}
                              onMouseOver={(e) =>
                                (e.target.style.borderColor = "#26a69a")
                              }
                              onMouseOut={(e) =>
                                (e.target.style.borderColor = "#9e9e9e")
                              }
                              onClick={(e) =>
                                (e.target.style.borderColor = "#26a69a")
                              }
                              autocomplete="off"
                            />
                            <ErrorMessage
                              name="consumer_id"
                              render={(msg) => (
                                <div className="text-danger">{msg}</div>
                              )}
                            />
                          </div>
                        </Grid>
                        <Grid item lg={3} xs={12}>
                          <div>
                            {loading ? (
                              <div
                                className="spinner-border"
                                role="status"
                                style={{ color: "rgba(40, 40, 40, 0.8)" }}
                              >
                                <span className="sr-only">Loading...</span>
                              </div>
                            ) : (
                              <div>
                                <Button
                                  className="btn my-2 w-100"
                                  type="submit"
                                  style={{
                                    color: "white",
                                    fontWeight: 500,
                                    fontSize: "16px",
                                    textTransform: "capitalize",
                                    borderRadius: "10px",
                                    background:
                                      "linear-gradient(90deg, #0F5D33 5.56%, #383735 95.49%)",
                                    boxShadow:
                                      "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                                    padding: "10px 20px 10px 20px",
                                  }}
                                >
                                  Search
                                </Button>
                              </div>
                            )}
                          </div>
                        </Grid>
                      </Grid>
                    </Form>
                  )}
                </Formik>
                <div>
                  <CustomFileUpload handleChange={handleChange} />
                </div>
                <a
                  className="py-3"
                  href={`${process.env.PUBLIC_URL}/ConsumerUploadTemplate.xlsx`}
                  download="ConsumerUploadTemplate.xlsx"
                  style={{}}
                >
                  Download Template File
                </a>
                <div className="mt-3 mb-3">
                  {uploadLoading ? (
                    <div
                      className="spinner-border"
                      role="status"
                      style={{ color: "rgba(40, 40, 40, 0.8)" }}
                    >
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
                      <Button
                        className="btn my-2"
                        onClick={handleFileData}
                        style={{
                          color: "white",
                          fontWeight: 500,
                          fontSize: "16px",
                          background:
                            "linear-gradient(90deg, #433232 5.56%, #383735 95.49%)",
                          borderRadius: "5px",
                          boxShadow:
                            "0 2px 2px 0 rgba(0,0,0,0.14),0 3px 1px -2px rgba(0,0,0,0.12),0 1px 5px 0 rgba(0,0,0,0.2)",
                          textTransform: "capitalize",
                        }}
                      >
                        Parse & Validate
                      </Button>
                      {parsedRows && !isUploaded && parsedRows.some((r) => r.parsingErrors.length === 0) && (
                        <Button
                          className="btn my-2"
                          onClick={handleConfirmUpload}
                          style={{
                            color: "white",
                            fontWeight: 500,
                            fontSize: "16px",
                            background:
                              "linear-gradient(90deg, #0F5D33 5.56%, #1a7a45 95.49%)",
                            borderRadius: "5px",
                            boxShadow:
                              "0 2px 2px 0 rgba(0,0,0,0.14),0 3px 1px -2px rgba(0,0,0,0.12),0 1px 5px 0 rgba(0,0,0,0.2)",
                            textTransform: "capitalize",
                          }}
                        >
                          Confirm Upload ({parsedRows.filter((r) => r.parsingErrors.length === 0).length} valid rows)
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                {consumerData && (
                  <div
                    className="mx-2 fs-16 fw-500"
                    style={{ borderRadius: "10px" }}
                  >
                    <DataTable
                      customStyles={customStyles}
                      columns={[
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Consumer No
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div
                                title={value?.consumerno}
                              >
                                {value?.consumerno}
                              </div>
                            );
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              meterno
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div title={value?.meterno}>{value?.meterno}</div>
                            );
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Division
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div title={value?.division}>
                                {value?.division}
                              </div>
                            );
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Consumer Category
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div title={value?.consumercategory}>
                                {value?.consumercategory}
                              </div>
                            );
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Consumer Name
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div title={value?.consumername}>
                                {value?.consumername}
                              </div>
                            );
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Area
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return <div title={value?.area}>{value?.area}</div>;
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Address
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div title={value?.address}>{value?.address}</div>
                            );
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Phone
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div title={value?.phone}>{value?.phone}</div>
                            );
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Email
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div title={value?.email}>{value?.email}</div>
                            );
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Status
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div title={value?.status}>{value?.status}</div>
                            );
                          },
                          sortable: true,
                        },

                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Comments
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div title={value?.comments_}>
                                {value?.comments_}
                              </div>
                            );
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              SBI Unique Identifier
                            </span>
                          ),
                          selector: (row) => row,
                          cell: (value) => {
                            return (
                              <div title={value?.sbi_unique_identifier}>
                                {value?.sbi_unique_identifier}
                              </div>
                            );
                          },
                          sortable: true,
                        },
                      ]}
                      data={consumerData}
                      noDataComponent={
                        <div className="text-center fs-18 fw-600 m-auto mt-5">
                          There are no records to display!
                        </div>
                      }
                      pagination={consumerData?.length > 10 ? true : false}
                      paginationPerPage={10}
                    />
                  </div>
                )}
              </Grid>
            </Grid>

            {/* Upload Results Table */}
            <Grid container>
              <Grid item xs={12}>
                {uploadResults && (
                  <div className="mx-2 my-3">
                    <h6 className="fw-bold mb-2" style={{ fontSize: "18px" }}>
                      Upload Results
                      <span style={{ fontSize: "14px", fontWeight: "400", marginLeft: "10px" }}>
                        ({uploadResults.filter((r) => r.apiStatus === "Success").length} success,{" "}
                        {uploadResults.filter((r) => r.apiStatus !== "Success").length} failed)
                      </span>
                    </h6>
                    <DataTable
                      customStyles={customStyles}
                      columns={[
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Row #
                            </span>
                          ),
                          selector: (row) => row.rowNumber,
                          width: "80px",
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Consumer No
                            </span>
                          ),
                          selector: (row) => row.data?.consumerno || "-",
                          minWidth: "150px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Meter No
                            </span>
                          ),
                          selector: (row) => row.data?.meterno || "-",
                          minWidth: "150px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Division
                            </span>
                          ),
                          selector: (row) => row.data?.division || "-",
                          minWidth: "120px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Consumer Category
                            </span>
                          ),
                          selector: (row) => row.data?.consumercategory || "-",
                          minWidth: "160px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Consumer Name
                            </span>
                          ),
                          selector: (row) => row.data?.consumername || "-",
                          minWidth: "160px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Area
                            </span>
                          ),
                          selector: (row) => row.data?.area || "-",
                          minWidth: "120px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Address
                            </span>
                          ),
                          selector: (row) => row.data?.address || "-",
                          minWidth: "200px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Phone
                            </span>
                          ),
                          selector: (row) => row.data?.phone || "-",
                          minWidth: "130px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Email
                            </span>
                          ),
                          selector: (row) => row.data?.email || "-",
                          minWidth: "180px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Status
                            </span>
                          ),
                          selector: (row) => row.data?.status || "-",
                          minWidth: "120px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Meter Type
                            </span>
                          ),
                          selector: (row) => row.data?.metertype || "-",
                          minWidth: "130px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Comments
                            </span>
                          ),
                          selector: (row) => row.data?.comments_ || "-",
                          minWidth: "160px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Payment Division
                            </span>
                          ),
                          selector: (row) => row.data?.payment_division || "-",
                          minWidth: "150px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Meter Make
                            </span>
                          ),
                          selector: (row) => row.data?.meter_make || "-",
                          minWidth: "130px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              SBI Unique Identifier
                            </span>
                          ),
                          selector: (row) => row.data?.sbi_unique_identifier || "-",
                          minWidth: "180px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Old Consumer No
                            </span>
                          ),
                          selector: (row) => row.data?.oldConsumerno || "-",
                          minWidth: "150px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Old Meter No
                            </span>
                          ),
                          selector: (row) => row.data?.oldMeterno || "-",
                          minWidth: "150px",
                          wrap: true,
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Upload Status
                            </span>
                          ),
                          width: "130px",
                          cell: (row) => {
                            const status = row.apiStatus;
                            const color =
                              status === "Success"
                                ? "#28a745"
                                : status === "Skipped"
                                ? "#ffc107"
                                : status === "Pending"
                                ? "#17a2b8"
                                : "#dc3545";
                            return (
                              <span
                                style={{
                                  color: color,
                                  fontWeight: "600",
                                  fontSize: "13px",
                                }}
                              >
                                {status}
                              </span>
                            );
                          },
                          sortable: true,
                        },
                        {
                          name: (
                            <span className="font-weight-bold fs-13 text-uppercase">
                              Errors
                            </span>
                          ),
                          minWidth: "300px",
                          cell: (row) => {
                            const errors = [];
                            if (row.parsingErrors && row.parsingErrors.length > 0) {
                              errors.push(...row.parsingErrors.map((e) => `[Validation] ${e}`));
                            }
                            if (row.apiError) {
                              errors.push(`[API] ${row.apiError}`);
                            }
                            if (errors.length === 0) return <span style={{ color: "#28a745" }}>—</span>;
                            return (
                              <div
                                title={errors.join("; ")}
                                style={{
                                  color: "#dc3545",
                                  fontSize: "12px",
                                  whiteSpace: "normal",
                                  lineHeight: "1.4",
                                }}
                              >
                                {errors.map((err, i) => (
                                  <div key={i}>{err}</div>
                                ))}
                              </div>
                            );
                          },
                        },
                      ]}
                      data={uploadResults}
                      noDataComponent={
                        <div className="text-center fs-18 fw-600 m-auto mt-5">
                          No upload results to display
                        </div>
                      }
                      pagination={uploadResults?.length > 10}
                      paginationPerPage={10}
                      conditionalRowStyles={[
                        {
                          when: (row) => row.apiStatus === "Success",
                          style: { backgroundColor: "#f0fff4" },
                        },
                        {
                          when: (row) => row.apiStatus === "Error" || row.apiStatus === "Skipped",
                          style: { backgroundColor: "#fff5f5" },
                        },
                        {
                          when: (row) => row.apiStatus === "Pending",
                          style: { backgroundColor: "#f0f9ff" },
                        },
                      ]}
                    />
                  </div>
                )}
              </Grid>
            </Grid>
          </Grid>
        </div>
        {/* </div> */}
        <NewFooter />
      </div>
    </>
  );
};

export default UploadData;
